/**
 * @fileoverview Hook to fetch and group today's orders by orderId.
 * @module features/daily-summary/hooks/useTodaysOrders
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { salesService } from '@/features/orders/api/salesService'
import type { SaleDTO } from '@/features/orders/schemas/salesSchema'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'

/** A single line item within an order group. */
export interface OrderLineItem {
  sku: string
  /** variantSkuKey (UUID) used for routing to product statistics page. */
  skuKey: string | null
  name: string
  imageUrl: string | null
  quantity: number
  amount: number
}

/** A group of line items sharing the same orderId. */
export interface OrderGroup {
  orderId: string
  /** User-facing order ID: OMS order ID (BaseLinker) when available, otherwise store order number. */
  displayOrderId: string
  country: string
  /** Human-readable order source (store name, OMS source name, or 'Allegro'/'Personal'). */
  orderSource: string | null
  total: number
  lines: OrderLineItem[]
  /** Highest sale ID in the group — used as a recency proxy. */
  maxSaleId: number
}

export interface UseTodaysOrdersResult {
  orders: OrderGroup[]
  totalOrders: number
  totalRevenue: number
  isLoading: boolean
  isError: boolean
}

export function groupSalesByOrder(sales: SaleDTO[]): OrderGroup[] {
  const map = new Map<string, OrderGroup>()

  for (const sale of sales) {
    // Use orderKey (DB PK, guaranteed unique) for grouping, falling back to omsOrderId then orderId
    const key = sale.orderKey ?? sale.omsOrderId ?? sale.orderId
    const existing = map.get(key)
    const line: OrderLineItem = {
      sku: String(sale.sku?.id ?? ''),
      skuKey: sale.sku?.productExternalId ?? null,
      name: sale.sku?.name ?? sale.sourceProductName ?? 'Unknown',
      imageUrl: sale.sku?.imageUrl ?? null,
      quantity: sale.quantity,
      amount: sale.reportingTotal,
    }

    if (existing) {
      existing.lines.push(line)
      existing.total += sale.reportingTotal
      existing.maxSaleId = Math.max(existing.maxSaleId, sale.id)
    } else {
      map.set(key, {
        orderId: key,
        displayOrderId: sale.omsOrderId ?? sale.orderId,
        country: sale.country,
        orderSource: sale.orderSource ?? null,
        total: sale.reportingTotal,
        lines: [line],
        maxSaleId: sale.id,
      })
    }
  }

  return Array.from(map.values()).sort((a, b) => b.maxSaleId - a.maxSaleId)
}

/**
 * Fetches today's sales and groups them by orderId.
 *
 * @returns Grouped orders, totals, and loading/error states
 * @example
 * ```ts
 * const { orders, totalOrders, isLoading } = useTodaysOrders()
 * ```
 */
export function useTodaysOrders(): UseTodaysOrdersResult {
  const todayStr = toDateOnlyStringLocal(new Date())

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sales', 'daily-summary', todayStr],
    queryFn: () =>
      salesService.getSales({
        startDate: todayStr,
        endDate: todayStr,
        sort: 'date',
        direction: 'desc',
        size: 200,
        page: 0,
      }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  const orders = useMemo(
    () => groupSalesByOrder(data?.content ?? []),
    [data?.content],
  )

  const totalRevenue = useMemo(
    () => orders.reduce((sum, o) => sum + o.total, 0),
    [orders],
  )

  return {
    orders,
    totalOrders: orders.length,
    totalRevenue,
    isLoading,
    isError,
  }
}
